import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { currentRoute } from '@game-platform/theme-constructor';
import type { ApiClient, IframePoolOrderItem, VolatilityLevel } from '../api';
import { gameEvents } from '../postMessage';
import { useBackBlocker, useBackHandler } from '../backStack';
import { useReveal } from './useReveal';
import { PackReveal } from './PackReveal';
import { RevealBackground } from './RevealBackground';
import { RevealToggles } from './RevealToggles';
import { Button } from '../design-system/Button';
import { ChevronLeft, ChevronRight, Loader, useBodyScrollLock } from '../design-system';
import { useIsSleek } from '../theme';
import { BalancePill } from '../wallet';
import { useVoluntaryTopUp } from '../payments/useVoluntaryTopUp';
import { SleekRevealTopBar } from './sleek/SleekRevealTopBar';
import { UnopenedPacksSheet } from './sleek/UnopenedPacksSheet';
import { useSleekExit } from './sleek/useSleekExit';

export interface RevealScreenProps {
  client: ApiClient;
  orderId: string;
  onClose: () => void;
  /** Buyback "Accept Offer & Rip Again" path — buy another pack from
   *  the same offer. */
  onBuyAgain: () => void;
  /** "Create Shipping" path on the winner screen + the route taken
   *  when the reveal screen closes back to the pack-detail page. */
  onShowPack: () => void;
  /** "Confirm Shipping" on the winner modal → open the delivery flow for
   *  that item. */
  onShip?: (item: IframePoolOrderItem) => void;
  /** Volatility the player selected on the pack page; forwarded to
   *  "Open Next Pack" so a fresh pack keeps the same odds. */
  volatility?: VolatilityLevel;
  /** The order's last pack ends with "Done" and leaves the reveal instead of
   *  offering another pack from the same offer. For an offer that cannot be
   *  bought again at its listed price; sleek always ends this way. */
  endWithDone?: boolean;
}

/**
 * Full‑screen reveal view rendered on the Pack page when `order_id` is
 * present in the URL. Hosts the slider, top bar, ambient background and
 * delegates per‑pack flow to `<PackReveal>`.
 *
 * Multi‑pack navigation (forward‑only):
 *   - `activeIndex` = currently shown pack
 *   - advance is one‑way to the right, up to the first unopened pack
 *     (`maxReachable`) — no going back to already‑opened packs (casino
 *     "keep playing forward"), and the advance is suppressed while an
 *     Open Next Pack loop / shuffle is live.
 */
export function RevealScreen({
  client,
  orderId,
  onClose,
  onBuyAgain,
  onShowPack,
  onShip,
  volatility,
  endWithDone = false,
}: RevealScreenProps) {
  const { order, loading, error, updateItem, refetch } = useReveal(client, orderId);
  // Stripe → interactive balance pill (deposit flow); seamless → undefined →
  // read-only display.
  const onBalanceTap = useVoluntaryTopUp();
  const [activeIndex, setActiveIndex] = useState(0);
  // Locked while the current pack's winner screen is in its Open Next Pack
  // loop or a shuffle is mid-flight: navigating then would remount
  // PackReveal and destroy the in-flight loop pack. WinnerStep reports it up.
  const [navLocked, setNavLocked] = useState(false);

  // Tell the Design Constructor (if we're embedded) that the
  // operator is now editing the reveal screen — pathname is synthetic
  // (`/reveal`); the real URL still says `/`. Listener in
  // `useIframeScene` maps it to the `reveal` scene, which gates which
  // components show up in the sidebar. On unmount we restore the
  // pathname captured at MOUNT — reading it in the cleanup would
  // pick up any unrelated navigation that happened mid-reveal. A page
  // that is not at its own URL (the custom pack lab) has its route here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;
    const originalPathname = currentRoute();
    window.parent.postMessage(
      { type: 'design-constructor-route', pathname: '/reveal' },
      '*',
    );
    return () => {
      window.parent.postMessage(
        { type: 'design-constructor-route', pathname: originalPathname },
        '*',
      );
    };
  }, []);

  // Lock the document scroll while the reveal owns the viewport. The
  // reveal's entrance/exit transforms (title slide, bottom fly-down) push
  // content briefly past the screen edges; without this the iframe document
  // grows a scrollbar mid-animation.
  //
  // `body` goes through the SHARED, reference-counted lock rather than a
  // save/restore of our own: the shipping modal opens on top of the reveal
  // and takes the same lock, and "Confirm Shipping" closes the modal and
  // leaves the reveal in one batch. React unmounts a deleted subtree
  // parent-first, so our restore ran BEFORE the modal's — and the modal then
  // put back the `hidden` it had snapshotted from us, leaving the page (the
  // delivery form we navigate straight into) permanently unscrollable.
  useBodyScrollLock();
  // `html` has no other owner, so a plain save/restore is safe here.
  useEffect(() => {
    const html = document.documentElement;
    const prevHtml = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
    };
  }, []);

  const items = useMemo(() => order?.poolItems ?? [], [order?.poolItems]);

  // Start at the first unopened item (or the last if all opened).
  useEffect(() => {
    if (!order) return;
    const firstUnopened = order.poolItems.findIndex((i) => !i.openedAt);
    setActiveIndex(firstUnopened === -1 ? Math.max(order.poolItems.length - 1, 0) : firstUnopened);
    gameEvents.revealStarted(order.id, order.poolItems.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  const maxReachable = useMemo(() => {
    if (items.length === 0) return 0;
    for (let i = 0; i < items.length; i++) {
      const cur = items[i];
      if (cur && !cur.openedAt) return i;
    }
    return items.length - 1;
  }, [items]);

  // Forward-only: advance to the right only (up to the first unopened
  // pack), never back. Suppressed while a loop/shuffle is live so we don't
  // remount over an in-flight Open Next Pack.
  const canNext = !navLocked && activeIndex < maxReachable;
  const goNext = () => canNext && setActiveIndex((i) => i + 1);

  // Does this purchase still have a sealed pack ahead of the current one?
  // While it does, the winner screen's "Open Next Pack" CTA advances to it
  // (open what you already bought) instead of buying a brand-new pack.
  const hasNextSealedPack = useMemo(
    () => items.some((it, i) => i > activeIndex && !it.openedAt),
    [items, activeIndex],
  );

  const activeItem = items[activeIndex];

  const isSleek = useIsSleek();
  const handleBack = () => {
    gameEvents.revealComplete(orderId);
    onClose();
  };

  // Sleek asks before leaving sealed packs behind (see useSleekExit).
  const sleekExit = useSleekExit({ client, orderId, items, onExit: handleBack });
  const requestClose = isSleek ? sleekExit.requestExit : handleBack;

  // Host back button (modules/backStack) = Exit, except mid Open Next Pack
  // loop / shuffle, where leaving would drop the in-flight purchase.
  useBackHandler(true, requestClose, 'overlay');
  useBackBlocker(navLocked);

  if (loading) {
    return (
      <OverlayHost>
        <LoadingState />
      </OverlayHost>
    );
  }

  if (error || !order) {
    return (
      <OverlayHost>
        <ErrorState
          message={error ?? 'Failed to load order'}
          onClose={handleBack}
        />
      </OverlayHost>
    );
  }

  if (!activeItem) {
    return (
      <OverlayHost>
        <ErrorState message="This order has no items" onClose={handleBack} />
      </OverlayHost>
    );
  }

  return (
    <OverlayHost>
      {/* Slider rail. For now we show only the active pack — web does the
          same (centered single slide) even though it peeks neighbours at
          certain breakpoints. That peek can be layered on later. */}
      <div className="relative flex-1 w-full">
        <PackReveal
          key={activeItem.id}
          client={client}
          orderId={orderId}
          offer={order.poolOffer}
          item={activeItem}
          onRevealed={(updated) => updateItem(String(activeItem.id), updated)}
          refetchOrder={refetch}
          onBuyAgain={onBuyAgain}
          onShowPack={onShowPack}
          {...(onShip ? { onShip } : {})}
          {...(volatility ? { volatility } : {})}
          hasNextSealedPack={hasNextSealedPack}
          onAdvanceToNextSealed={goNext}
          // Sleek, and any offer that cannot be bought again at its listed price
          // (`endWithDone`: the custom pack lab), end the order's last pack by
          // leaving (Done / after Sell or Vault, partner feedback 2026-10-01)
          // instead of buying another.
          {...(isSleek || endWithDone ? { onDone: handleBack } : {})}
          onLoopActiveChange={setNavLocked}
        />

        {/* Sleek (partner mockup) has no arrow: its "Rip next pack" CTA
            advances through the order instead. */}
        {canNext && !isSleek && <NavArrow direction="right" onClick={goNext} />}

        {/* Top bar: the player's switches (skip, mute), the balance, Exit.
            Sleek has its own (SleekRevealTopBar: close + "Pack i/N" only).
            Mobile (< 600px): opposite corners — Exit flush-left, the
            switches + balance flush-right — via justify-between and `order`.
            At ≥600px everything groups to the right (justify-end) in DOM
            order, the balance right beside Exit. An operator can hide the
            pill on this screen (`--co-BalancePill-revealDisplay`, see
            `surface`); the switches keep the group in place, so Exit does not
            move when it goes. The winner title occupies
            its own full-width row beneath them (the header reserves their
            height via STAGE_TOP_RESERVE). This screen has no header of its
            own, and the reveal is exactly where the player wants to watch
            their balance.

            Exit leaves the pack-opening flow back to the pack page; it uses
            the themable `glass-frosted` Button variant (Figma 1251:1122) so
            the Design Constructor can restyle it via `--co-Button-glass-frosted*`.
            The pill is interactive (opens the deposit flow) for stripe
            operators and a read-only display for seamless ones. */}
        {isSleek ? (
          <SleekRevealTopBar
            onClose={requestClose}
            // Nothing sealed left = the last pack is open: Vault / Sell only.
            canClose={sleekExit.sealedCount > 0}
            packIndex={activeIndex}
            packCount={items.length}
          />
        ) : (
          <div className="absolute top-4 inset-x-4 z-30 flex items-center justify-between @[600px]:justify-end @[600px]:gap-3">
            <div className="order-2 flex items-center gap-2 @[600px]:order-none @[600px]:gap-3">
              <RevealToggles animation={order.poolOffer.animation} />
              <BalancePill surface="reveal" fixedSize {...(onBalanceTap ? { onClick: onBalanceTap } : {})} />
            </div>
            <Button
              variant="glass-frosted"
              size="md"
              onClick={handleBack}
              className="order-1 @[600px]:order-none"
            >
              Exit
            </Button>
          </div>
        )}

        <AnimatePresence>
          {isSleek && sleekExit.sheetOpen && (
            <UnopenedPacksSheet
              vaulting={sleekExit.vaulting}
              onCloseAndVault={() => void sleekExit.closeAndVault()}
              onContinue={sleekExit.continueRipping}
            />
          )}
        </AnimatePresence>
      </div>
    </OverlayHost>
  );
}

function OverlayHost({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      // `@container` enables Ready / Winner step content to branch on
      // our own width rather than the viewport — iframe can be embedded
      // at narrower dimensions than the window itself. `overflow-hidden`
      // clips the reveal animations (title slide / bottom fly-down) at the
      // screen edges so their transforms can't spawn a page scrollbar.
      //
      // `--reveal-pack-max-w` caps the slot-tile width by the available
      // HEIGHT (viewport minus the 88px Shuffle-CTA gutter plus breathing
      // room), converted through the pack's 421/526 aspect ratio. Wide but
      // short embeds used to crop the pack art vertically — width-only
      // breakpoints never shrank the tile. The final ×0.8 keeps the pack
      // at ~80% of the free height so it starts shrinking earlier and
      // never reads as oversized on short embeds. Consumed by SlotTile
      // and RevealGlowOverlay via min(<breakpoint>, var). The host safe
      // area is subtracted for the same reason: `safe-area-pad` takes that
      // height away from the stage, so a cap computed on the full viewport
      // would start cropping the pack again. `inset-0` stays — this is a
      // stage, not a dim: RevealBackground must reach the screen edges
      // (an `absolute inset-0` child resolves against the padding box),
      // only the content is pushed in.
      className="@container safe-area-pad fixed inset-0 z-50 flex flex-col overflow-hidden text-white [--reveal-pack-max-w:calc((100dvh_-_120px_-_var(--page-safe-top)_-_var(--page-safe-bottom))*421/526*0.8)]"
      role="dialog"
      aria-modal="true"
    >
      <RevealBackground />
      <div className="relative z-10 flex flex-1 flex-col">{children}</div>
    </motion.div>
  );
}

function NavArrow({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const isLeft = direction === 'left';
  // Wrapper owns the absolute positioning because Button's BASE class
  // declares `position: relative` and would otherwise win over an
  // `absolute` passed via className, flipping `right-4` into an in-flow
  // leftward offset.
  return (
    <div
      className={`absolute top-1/2 z-20 -translate-y-1/2 ${isLeft ? 'left-4' : 'right-4'}`}
    >
      <Button
        variant="glass-icon"
        size="lg"
        aria-label={isLeft ? 'Previous pack' : 'Next pack'}
        onClick={onClick}
      >
        {isLeft ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
      </Button>
    </div>
  );
}

function LoadingState() {
  return (
    // The loader is currentColor and OverlayHost is text-white, so it needs no
    // colour of its own.
    <div className="flex flex-1 items-center justify-center">
      <Loader />
    </div>
  );
}

function ErrorState({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
      <h2 className="type-headline font-bold">Something went wrong</h2>
      <p className="type-subhead opacity-80 max-w-[320px]">{message}</p>
      <Button variant="primary" size="md" onClick={onClose}>
        Back to pack
      </Button>
    </div>
  );
}
